import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Text } from "@andrum/ui";
import type { ExperimentCategory } from "@andrum/schemas";
import {
  scienceForCategory,
  scienceForSlug,
  type EvidenceLevel,
} from "@/features/learn/data/science-notes";
import { scienceNoteForSlug } from "@/i18n/catalog";
import { cn } from "@/lib/utils";

function EvidenceBadge({ level }: { level: EvidenceLevel }) {
  const { t } = useTranslation();
  return (
    <span className="inline-flex rounded-full bg-primary-soft px-2.5 py-0.5 text-xs font-medium text-primary">
      {t(`learn.evidence.${level}`)}
    </span>
  );
}

function normalizeCopy(s: string): string {
  return s
    .toLowerCase()
    .replace(/\s+/g, " ")
    .replace(/[“”"'´`]/g, "")
    .trim();
}

/** Skip a second paragraph when it mostly repeats the first. */
function isRedundant(a: string, b: string): boolean {
  const na = normalizeCopy(a);
  const nb = normalizeCopy(b);
  if (!na || !nb) return true;
  if (na === nb) return true;
  if (na.includes(nb) || nb.includes(na)) return true;
  if (na.slice(0, 48) === nb.slice(0, 48)) return true;
  return false;
}

/** Compact expandable block on experiment detail */
export function ExperimentScienceBlock({
  slug,
  category,
  rationale,
  personalLine,
}: {
  slug: string;
  category: ExperimentCategory;
  rationale: string;
  /** Soft line tying experiment to goals / last check-in */
  personalLine?: string | null;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const meta = scienceForSlug(slug, category);
  const localized = scienceNoteForSlug(slug);
  const whyItMayHelp =
    localized?.why ?? t(`learn.categories.${category}.headline`);
  const whatWeKnow =
    localized?.know ?? t(`learn.categories.${category}.body`);
  const categoryBody = t(`learn.categories.${category}.body`);
  const honestNote = localized?.dont ?? t("experiment.honestNote");
  const evidenceBlurb = t(`learn.evidence.${meta.evidenceLevel}Blurb`);
  const showWhyHeadline =
    Boolean(whyItMayHelp) && !isRedundant(rationale, whyItMayHelp);

  return (
    <section className="space-y-3 rounded-2xl border border-border bg-surface-muted/60 p-4">
      <div className="flex flex-wrap items-center gap-2">
        <Text variant="label">{t("experiment.scienceLabel")}</Text>
        <EvidenceBadge level={meta.evidenceLevel} />
      </div>

      {personalLine ? (
        <div className="rounded-xl border border-primary/15 bg-primary-soft/50 px-3 py-2.5">
          <Text variant="label" className="mb-1 text-primary">
            {t("experiment.forYouLabel")}
          </Text>
          <Text variant="body" className="text-sm leading-relaxed">
            {personalLine}
          </Text>
        </div>
      ) : null}

      <div className="space-y-1.5">
        <Text variant="label">{t("experiment.whyThis")}</Text>
        <Text variant="body" className="text-sm leading-relaxed">
          {rationale}
        </Text>
      </div>

      {showWhyHeadline ? (
        <Text variant="subtle" className="text-sm leading-relaxed">
          {whyItMayHelp}
        </Text>
      ) : null}

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="text-sm font-medium text-primary"
        aria-expanded={open}
      >
        {open ? t("experiment.hideMore") : t("experiment.readMore")}
      </button>
      {open ? (
        <div className="space-y-4 border-t border-border/80 pt-3">
          <div className="space-y-1.5">
            <Text variant="label">{t("experiment.whatWeKnow")}</Text>
            <Text variant="body" className="text-sm leading-relaxed">
              {whatWeKnow}
            </Text>
            {!isRedundant(whatWeKnow, evidenceBlurb) ? (
              <Text variant="subtle" className="text-sm leading-relaxed">
                {evidenceBlurb}
              </Text>
            ) : null}
          </div>

          <div className="space-y-1.5">
            <Text variant="label">{t("experiment.howItFits")}</Text>
            <Text variant="body" className="text-sm leading-relaxed">
              {t("experiment.howItFitsBody")}
            </Text>
            {!isRedundant(whatWeKnow, categoryBody) ? (
              <Text variant="subtle" className="text-sm leading-relaxed">
                {categoryBody}
              </Text>
            ) : null}
          </div>

          <div className="space-y-1.5">
            <Text variant="label">{t("experiment.honestTitle")}</Text>
            <Text variant="body" className="text-sm leading-relaxed">
              {honestNote}
            </Text>
            <Text
              variant="caption"
              className="leading-relaxed text-foreground-muted"
            >
              {t("experiment.disclaimer")}
            </Text>
          </div>
        </div>
      ) : null}
    </section>
  );
}

export function CategoryScienceCard({
  category,
  defaultOpen = false,
}: {
  category: ExperimentCategory;
  defaultOpen?: boolean;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(defaultOpen);
  const meta = scienceForCategory(category);
  const base = `learn.categories.${category}`;

  const citations = [1, 2]
    .map((i) => ({
      label: t(`${base}.c${i}`),
      url: meta.citationUrls[i - 1],
    }))
    .filter((c) => c.label && !c.label.startsWith("learn.categories."));

  return (
    <article className="space-y-2 rounded-2xl border border-border bg-surface-raised p-4 shadow-sm">
      <button
        type="button"
        className="flex w-full items-start justify-between gap-3 text-left"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <div className="space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <Text variant="heading" as="h3">
              {t(`${base}.title`)}
            </Text>
            <EvidenceBadge level={meta.evidenceLevel} />
          </div>
          <Text variant="subtle">{t(`${base}.headline`)}</Text>
        </div>
        <span
          className="shrink-0 text-lg leading-none text-primary"
          aria-hidden
        >
          {open ? "−" : "+"}
        </span>
      </button>
      {open ? (
        <div className="space-y-3 pt-1">
          <Text variant="body" className="leading-relaxed">
            {t(`${base}.body`)}
          </Text>
          <ul className="space-y-1.5">
            {[1, 2, 3].map((i) => (
              <li
                key={i}
                className="flex gap-2 text-sm text-foreground-muted"
              >
                <span className="font-medium text-primary" aria-hidden>
                  ·
                </span>
                <span>{t(`${base}.m${i}`)}</span>
              </li>
            ))}
          </ul>
          <Text variant="caption">
            {t("learn.tradition", { text: t(`${base}.tradition`) })}
          </Text>
          {citations.length > 0 ? (
            <div className="space-y-1.5 pt-1">
              <Text variant="label">{t("learn.sources")}</Text>
              <ul className="space-y-1">
                {citations.map((c) => (
                  <li key={c.label} className="text-xs text-foreground-muted">
                    {c.url ? (
                      <a
                        href={c.url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-primary underline-offset-2 hover:underline"
                      >
                        {c.label}
                      </a>
                    ) : (
                      <span>· {c.label}</span>
                    )}
                  </li>
                ))}
              </ul>
              <Text variant="caption" className="leading-relaxed">
                {t("learn.sourcesNote")}
              </Text>
            </div>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}

export function MethodOverviewCard({ className }: { className?: string }) {
  const { t } = useTranslation();
  return (
    <div
      className={cn(
        "space-y-3 rounded-2xl border border-border bg-primary-soft/40 p-4",
        className
      )}
    >
      <Text variant="label">{t("learn.methodLabel")}</Text>
      <Text variant="heading" as="h2">
        {t("learn.methodCardTitle")}
      </Text>
      <Text variant="subtle" className="leading-relaxed">
        {t("learn.methodCardBody")}
      </Text>
    </div>
  );
}
